// SiegerehrungDisplayPage.jsx — Vollbild fuer zweiten Bildschirm / Beamer.
//
// Seit 03.10.2026 (Aufgabe 149) liest der Bildschirm unter
// /siegerehrung-display/:turnierId vom Server (GET /api/siegerehrung/turnier/
// :id/anzeige, alle 2 s) — er laeuft damit auf JEDEM Geraet: Beamer-Laptop,
// Hallen-TV, Tablet. Vorher ging er nur ueber localStorage im selben Browser
// wie die Steuerung. Der lokale Weg (storage-Event + BroadcastChannel) bleibt
// als Sofort-Kanal, wenn beide Fenster auf demselben Rechner laufen.
// Ohne Vollbild wird trotzdem angezeigt (Fernseher ohne Vollbild-Funktion
// blieben sonst auf der Einrichtungsseite haengen); die Einrichtung ist nur
// noch eine Leiste am unteren Rand.
//
// Gestaltung: styles/leinwand.css, dieselbe wie die uebrigen Leinwaende.
// Kopfband, Fussleiste und Typografie kommen von dort; das Podest ist das
// Eigene dieser Anzeige.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/leinwand.css';
import './SiegerehrungDisplayPage.css';
// Platzabzeichen statt 🥇🥈🥉 — dieselben wie auf der Formen-Leinwand.
import Platz from '../components/leinwand/Platz';

const CHANNEL = 'tda-siegerehrung';

const NEU_LADEN_MS = 2000;

export default function SiegerehrungDisplayPage() {
  const params = useParams();
  const turnierId = params.turnierId || (() => {
    try { return localStorage.getItem('tda-selected-turnier-id'); } catch { return null; }
  })();
  const [data, setData]           = useState(null);
  const [turnierName, setTurnierName] = useState('');
  const letzteVersion = useRef(null);
  // Einrichtungsleiste: nach 12 s ohne Maus/Touch weg, damit sie am Beamer
  // nicht über der Anzeige stehen bleibt; jede Bewegung holt sie zurück.
  const [leisteSichtbar, setLeisteSichtbar] = useState(true);
  useEffect(() => {
    let timer = setTimeout(() => setLeisteSichtbar(false), 12000);
    const zeigen = () => {
      setLeisteSichtbar(true);
      clearTimeout(timer);
      timer = setTimeout(() => setLeisteSichtbar(false), 12000);
    };
    window.addEventListener('mousemove', zeigen);
    window.addEventListener('touchstart', zeigen);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', zeigen);
      window.removeEventListener('touchstart', zeigen);
    };
  }, []);
  const letzterSchluessel = useRef('');
  const [animKey, setAnimKey]     = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow hidden
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  // Auto-Vollbild beim Laden (klappt wenn via Popup aus User-Geste geöffnet)
  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fullscreen-Status tracken
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }
  };

  // Chat-Widget und externe Elemente verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT','STYLE','LINK','META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const LS_KEY = 'tda-siegerehrung-state';

    const applyState = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed?.type === 'show' && parsed.kategorie) {
          const schluessel = `${parsed.kategorie.typ}-${parsed.kategorie.kategorie_id}`;
          setData({ kategorie: parsed.kategorie, idx: parsed.idx, total: parsed.total });
          if (schluessel !== letzterSchluessel.current) setAnimKey(k => k + 1);
          letzterSchluessel.current = schluessel;
        } else {
          setData(null);
          letzterSchluessel.current = '';
        }
      } catch (e) {}
    };

    // 1) Sofort lesen beim Öffnen
    applyState(localStorage.getItem(LS_KEY));

    // 2) storage-Event: feuert in ALLEN anderen Fenstern wenn localStorage sich ändert
    //    (zuverlässiger als BroadcastChannel in Safari)
    const onStorage = (e) => {
      if (e.key === LS_KEY) applyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    // 3) BroadcastChannel als zusätzlicher Kanal (für gleichen Tab)
    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => applyState(e.data);
    } catch (e) {}

    // 4) Server: der Weg fuer jedes andere Geraet. Nur bei neuer Version
    //    uebernehmen, sonst wuerde der Server einen gerade lokal gezeigten
    //    Stand mit einem 2 s alten ueberschreiben.
    let aus = false;
    const holen = async () => {
      if (!turnierId) return;
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`/api/siegerehrung/turnier/${turnierId}/anzeige`, {
          cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) return;
        const d = await r.json();
        if (aus) return;
        if (d.turnier_name) setTurnierName(d.turnier_name);
        if (d.version !== letzteVersion.current) {
          letzteVersion.current = d.version;
          applyState(d);
        }
      } catch (e) { /* naechster Versuch in 2 s */ }
    };
    holen();
    const iv = setInterval(holen, NEU_LADEN_MS);

    return () => {
      aus = true;
      clearInterval(iv);
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, [turnierId]);

  return (
    <>
      {!data
        ? <StandbyScreen onFullscreen={toggleFullscreen} turnierName={turnierName} />
        : <PodiumDisplay key={animKey} data={data} onFullscreen={toggleFullscreen} />}
      {!isFullscreen && leisteSichtbar && <SetupLeiste onFullscreen={toggleFullscreen} turnierId={turnierId} />}
    </>
  );
}

// ── Einrichtungsleiste (nur ohne Vollbild) ──────────────────────────
function SetupLeiste({ onFullscreen, turnierId }) {
  const [copied, setCopied] = useState(false);
  const displayUrl = `${window.location.origin}/siegerehrung-display${turnierId ? `/${turnierId}` : ''}`;
  const copyUrl = () => {
    navigator.clipboard?.writeText(displayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };
  return (
    <div className="sdp-leiste">
      <button className="sdp-leiste-btn" onClick={onFullscreen}>Vollbild starten</button>
      <span className="sdp-leiste-text">
        {turnierId
          ? <>Auf einem anderen Gerät (Beamer, Hallen-TV) diese Adresse öffnen: <strong>{displayUrl}</strong></>
          : 'Kein Turnier gewählt: Bildschirm aus dem Dashboard heraus öffnen.'}
      </span>
      {turnierId && <button className="sdp-leiste-btn leise" onClick={copyUrl}>{copied ? 'Kopiert' : 'Adresse kopieren'}</button>}
    </div>
  );
}

// ── Standby ───────────────────────────────────────────────────────────
function StandbyScreen({ onFullscreen, turnierName }) {
  return (
    <div className="lw sdp" onClick={onFullscreen} title="Klicken für Vollbild">
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">{turnierName || 'Tiger & Dragon Association'}</div>
        </div>
        <div className="lw-kopf-titel">表彰 SIEGEREHRUNG</div>
        <div className="lw-kopf-status wartet">BEREIT</div>
      </div>

      <div className="lw-buehne">
        <div className="lw-kanji wasserzeichen" aria-hidden>武</div>
        <div className="lw-name">TDA</div>
        <div className="lw-bereit-text">BEREIT</div>
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Tiger &amp; Dragon Association International</div>
        <div className="lw-fuss-wert" />
      </div>
    </div>
  );
}

// ── Podium Display ────────────────────────────────────────────────────
function PodiumDisplay({ data, onFullscreen }) {
  const { kategorie, idx, total } = data;
  const platz1 = kategorie.plaetze.find(p => p.platzierung === 1);
  const platz2 = kategorie.plaetze.find(p => p.platzierung === 2);
  const platz3 = kategorie.plaetze.find(p => p.platzierung === 3);
  const weiteres = kategorie.plaetze
    .filter(p => p.platzierung > 3)
    .sort((a, b) => a.platzierung - b.platzierung);

  // Eine Saeule je Platz: die Person steht auf ihrem Sockel, alle Sockel enden
  // auf derselben Linie. Frueher lagen Personen und Sockel in zwei getrennten
  // Reihen, und die Staffelung stand auf dem Kopf -- der Dritte oben, der
  // Sieger unten.
  const renderSaeule = (platz, medalLabel, rang, delay) => (
    <div className={`lw-podest-saeule lw-podest-saeule--${rang}${platz ? '' : ' leer'}`} style={{ '--delay': delay }}>
      <div className="lw-podest-person">
        <div className="sdp-medal-wrap">
          <Platz platz={rang} gross />
        </div>
        {platz ? (
          <>
            <div className="lw-podest-name">
              <span className="lw-podest-vorname">{platz.vorname}</span>
              <span className="lw-podest-nachname">{platz.nachname}</span>
            </div>
            <div className="lw-podest-verein">{platz.verein_name}</div>
            {platz.punkte != null && (
              <div className="lw-podest-punkte">{parseFloat(platz.punkte).toFixed(2)} Pkt</div>
            )}
          </>
        ) : (
          <div className="sdp-empty-slot">{medalLabel}</div>
        )}
      </div>
      <div className="lw-podest-sockel"><span>{rang}</span></div>
    </div>
  );

  const untertitel = [kategorie.division_code, kategorie.division_type].filter(Boolean).join(' · ');

  return (
    <div className="lw sdp" onClick={onFullscreen}>
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">{kategorie.kategorie_name}</div>
          {untertitel && <div className="lw-kopf-kat">{untertitel}</div>}
        </div>
        <div className="lw-kopf-titel">表彰 SIEGEREHRUNG</div>
        <div className="lw-kopf-status">{idx + 1} / {total}</div>
      </div>

      <div className="lw-buehne">
        <div className="lw-kanji wasserzeichen" aria-hidden>武</div>

        <div className="lw-podest">
          {renderSaeule(platz2, '2. Platz', 2, '0.2s')}
          {renderSaeule(platz1, '1. Platz', 1, '0.05s')}
          {renderSaeule(platz3, '3. Platz', 3, '0.35s')}
        </div>

        {weiteres.length > 0 && (
          <div className="sdp-weiteres">
            <div className="sdp-w-label">Weitere Platzierungen</div>
            <div className="sdp-w-list">
              {weiteres.map((p, i) => (
                <div key={i} className="sdp-w-entry">
                  <span className="sdp-w-platz">{p.platzierung}.</span>
                  <span className="sdp-w-name">{p.vorname} {p.nachname}</span>
                  <span className="sdp-w-club">{p.verein_name}</span>
                  {p.punkte != null && (
                    <span className="sdp-w-pts">{parseFloat(p.punkte).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Tiger &amp; Dragon Association</div>
        <div className="lw-fuss-wert" />
      </div>
    </div>
  );
}
